// Turnstile invisible (CLAUDE.md § Auth mode public) : distingue un humain d'un script AVANT
// POST /api/analyze, sans écran ni interaction. Ceci ne fait qu'obtenir le jeton à transmettre à
// l'API — la vérification qui compte est CÔTÉ SERVEUR (api/src/turnstile.ts) : un jeton jamais
// vérifié ne protège rien.
//
// Un jeton Turnstile est à usage unique côté `siteverify` : contrairement à un rendu de widget (une
// fois par page), il en faut un NOUVEAU par appel à /api/analyze — le widget est rendu une seule
// fois (`render`), puis RE-exécuté (`execute`) à chaque appel suivant, jamais re-rendu.
//
// API Turnstile et chargeur de script injectables (`ObtenirJetonTurnstileDeps`) pour que
// test/turnstileClient.test.ts exerce la logique de résolution (premier rendu vs exécutions
// suivantes, jeton frais par promesse en attente) sans navigateur ni script Cloudflare réel — même
// principe que web/src/capture/compression.ts (`DependancesCompression`).

export interface TurnstileRenderOptions {
  sitekey: string;
  size: 'invisible';
  callback: (token: string) => void;
  'error-callback': () => void;
  'expired-callback': () => void;
}

export interface TurnstileGlobal {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  execute(widgetId: string): void;
  reset(widgetId: string): void;
}

export interface ObtenirJetonTurnstileDeps {
  /** API Turnstile injectable — les tests n'appellent jamais le vrai script Cloudflare. */
  turnstile?: TurnstileGlobal;
  /** Charge le script officiel et résout une fois `window.turnstile` disponible. */
  chargerScript?: () => Promise<TurnstileGlobal>;
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let scriptCharge: Promise<TurnstileGlobal> | null = null;

/** Charge le script officiel une seule fois par page (mémoïsé), jamais si déjà présent. */
function chargerScriptOfficiel(): Promise<TurnstileGlobal> {
  const fenetre = window as unknown as { turnstile?: TurnstileGlobal };
  if (fenetre.turnstile) return Promise.resolve(fenetre.turnstile);
  if (!scriptCharge) {
    scriptCharge = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        const chargee = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
        if (chargee) resolve(chargee);
        else reject(new Error('Turnstile chargé mais window.turnstile absent'));
      };
      script.onerror = () => reject(new Error('Script Turnstile indisponible'));
      document.head.appendChild(script);
    });
  }
  return scriptCharge;
}

/**
 * Fabrique un obtenteur de jeton lié à UN conteneur/site key (fermé sur son propre état de widget,
 * pas de mutable au niveau module — plusieurs instances, par exemple en test, ne se marchent jamais
 * dessus). Chaque appel de la fonction rendue résout avec un jeton FRAIS.
 */
export function creerObtenteurJetonTurnstile(
  siteKey: string,
  conteneur: HTMLElement,
  deps: ObtenirJetonTurnstileDeps = {},
): () => Promise<string> {
  let widgetId: string | null = null;
  let enAttente: { resolve: (t: string) => void; reject: (e: Error) => void } | null = null;

  const callback = (token: string): void => {
    enAttente?.resolve(token);
    enAttente = null;
  };
  const erreur = (message: string) => (): void => {
    enAttente?.reject(new Error(message));
    enAttente = null;
  };

  return async function obtenirJeton(): Promise<string> {
    const ts = deps.turnstile ?? (await (deps.chargerScript ?? chargerScriptOfficiel)());
    return new Promise<string>((resolve, reject) => {
      enAttente = { resolve, reject };
      if (widgetId === null) {
        widgetId = ts.render(conteneur, {
          sitekey: siteKey,
          size: 'invisible',
          callback,
          'error-callback': erreur('Vérification anti-robot indisponible'),
          'expired-callback': erreur('Vérification anti-robot expirée'),
        });
      } else {
        ts.reset(widgetId);
        ts.execute(widgetId);
      }
    });
  };
}
